import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear storage if token is invalid
      localStorage.removeItem('kapta_token');
      localStorage.removeItem('kapta_user');
      
      // You might want to redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const profileService = {
  // Get user profile
  async getProfile() {
    try {
      const response = await api.get('/api/v1/profile/profile');
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to get profile');
    }
  },

  // Update profile
  async updateProfile(profileData) {
    try {
      const response = await api.put('/api/v1/profile/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  // Update password
  async updatePassword(passwordData) {
    try {
      const response = await api.put('/api/v1/profile/password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Update password error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to update password');
    }
  },

  // Delete account
  async deleteAccount() {
    try {
      const response = await api.delete('/api/v1/profile/profile');
      return response.data;
    } catch (error) {
      console.error('Delete account error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to delete account');
    }
  },

  // Get addresses
  async getAddresses() {
    try {
      const response = await api.get('/api/v1/profile/addresses');
      return response.data;
    } catch (error) {
      console.error('Get addresses error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to get addresses');
    }
  },

  // Add address
  async addAddress(addressData) {
    try {
      const response = await api.post('/api/v1/profile/addresses', addressData);
      return response.data;
    } catch (error) {
      console.error('Add address error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to add address');
    }
  },

  // Update address
  async updateAddress(addressId, addressData) {
    try {
      const response = await api.put(`/api/v1/profile/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      console.error('Update address error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to update address');
    }
  },

  // Delete address
  async deleteAddress(addressId) {
    try {
      const response = await api.delete(`/api/v1/profile/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      console.error('Delete address error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to delete address');
    }
  },

  // Set default address
  async setDefaultAddress(addressId) {
    try {
      const response = await api.put(`/api/v1/profile/addresses/${addressId}/default`);
      return response.data;
    } catch (error) {
      console.error('Set default address error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to set default address');
    }
  },

  // Get notification settings
  async getNotificationSettings() {
    try {
      const response = await api.get('/api/v1/profile/notifications');
      return response.data;
    } catch (error) {
      console.error('Get notifications error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to get notification settings');
    }
  },

  // Update notification settings
  async updateNotificationSettings(settings) {
    try {
      const response = await api.put('/api/v1/profile/notifications', settings);
      return response.data;
    } catch (error) {
      console.error('Update notifications error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to update notification settings');
    }
  },
};